'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import { Container } from '@/app/components/layouts/container';
import { Section } from '@/app/components/layouts/section';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';

function stripColgroups(html: string) {
  return html.replace(/<colgroup\b[^>]*>[\s\S]*?<\/colgroup>/gi, '');
}

interface BatteryTireTableProps {
  htmlContent?: string;
  className?: string;
  testId?: string;
}
export const BatteryTireTable = ({
  htmlContent,
  className,
  testId = 'battery-tire-table',
}: BatteryTireTableProps) => {
  const [theadHtml, setTheadHtml] = useState<string>('');
  const [tbodyHtml, setTbodyHtml] = useState<string>('');

  const tableHtml = useMemo(
    () => (htmlContent ? stripColgroups(htmlContent) : ''),
    [htmlContent]
  );

  useEffect(() => {
    let nextTheadHtml = '';
    let nextTbodyHtml = '';

    if (tableHtml) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(tableHtml, 'text/html');
      const table = doc.querySelector('table');
      const tbody = table?.querySelector('tbody');

      if (!table || !tbody) {
        nextTbodyHtml = tableHtml;
      } else {
        const tbodyRows = Array.from(tbody.rows);
        const rowsWithTh = tbodyRows.filter((tr) =>
          Array.from(tr.cells).some((cell) => cell.tagName === 'TH')
        );
        const rowsWithoutTh = tbodyRows.filter(
          (tr) => !Array.from(tr.cells).some((cell) => cell.tagName === 'TH')
        );

        const rowsWithThHtml = rowsWithTh.map((tr) => tr.outerHTML).join('');
        const rowsWithoutThHtml = rowsWithoutTh
          .map((tr) => tr.outerHTML)
          .join('');
        const classAttr = table.className ? ` class="${table.className}"` : '';

        nextTheadHtml = rowsWithThHtml
          ? `<table${classAttr}><thead>${rowsWithThHtml}</thead></table>`
          : '';
        nextTbodyHtml = `<table${classAttr}><tbody>${rowsWithoutThHtml}</tbody></table>`;
      }
    }

    queueMicrotask(() => {
      setTheadHtml(nextTheadHtml);
      setTbodyHtml(nextTbodyHtml);
    });
  }, [tableHtml]);

  const HEADER_REF = useRef<HTMLDivElement>(null);
  const BODY_REF = useRef<HTMLDivElement>(null);

  const handleScrollHeader = (scroll) => {
    const tableBody = BODY_REF.current as HTMLDivElement;
    if (tableBody) {
      tableBody.scrollLeft = scroll.target.scrollLeft;
    }
  };
  const handleScrollBody = (scroll) => {
    const tableHead = HEADER_REF.current as HTMLDivElement;
    if (tableHead) {
      tableHead.scrollLeft = scroll.target.scrollLeft;
    }
  };

  if (!htmlContent) return null;

  return (
    <Section
      size="none"
      overflow="none"
      className="overflow-visible"
      {...createDataAttributes({ test: `section-${testId}` })}
    >
      <Container size="desktop-only">
        <div
          className={cn('w-full pt-7 md:pt-10 xl:pt-16', className)}
          {...createDataAttributes({ test: `full-table-${testId}` })}
        >
          <div className="sticky top-14 z-10">
            <div
              ref={HEADER_REF}
              onScroll={handleScrollHeader}
              className={cn(
                'text-body-2-fixed table-styled',
                '[&_table]:rounded-b-none!'
              )}
              dangerouslySetInnerHTML={{ __html: theadHtml }}
            />
          </div>
          <div
            ref={BODY_REF}
            onScroll={handleScrollBody}
            className={cn(
              'text-body-2-fixed table-styled',
              '[&_table]:rounded-t-none!'
            )}
            dangerouslySetInnerHTML={{ __html: tbodyHtml }}
          />
        </div>
      </Container>
    </Section>
  );
};
